import { Wizard, WizardStep, WizardStepRenderParams } from '@factorialco/deprecated-design-system'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { WizardData } from 'modules/documents/components/BulkFiles/DocumentLocalStepsModal/types'
import FileUploader from 'modules/documents/components/BulkFiles/_steps/fileUploader'
import ManualSettings from 'modules/documents/components/BulkFiles/_steps/manualSettings'
import SplittingSettings from 'modules/documents/components/BulkFiles/_steps/splittingSettings'
import UploadSettings from 'modules/documents/components/BulkFiles/_steps/uploadSettings'
import filesStore from 'modules/documents/components/Documents/store'
import { useCreateUploadProcess } from 'modules/documents/hooks/useCreateUploadProcess'
import useDocumentContextlessPolicies from 'modules/documents/hooks/useDocumentContextlessPolicies'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { ALL_UPLOAD_TYPES, UPLOAD_TYPE } from 'modules/documents/lib/constants'

interface Props {
  onClose: () => void
  onDeleteFile: (i: number) => void
  onDropFiles: (files: File[]) => void
  onUploadFinished: () => Promise<unknown>
  folders: Folder[]
  isReadOnly: boolean
  wizardData: WizardData
  selectedFolder?: Folder
}

const DocumentLocalStepsModal = ({
  onClose,
  onDeleteFile,
  onDropFiles,
  onUploadFinished,
  isReadOnly,
  folders,
  wizardData,
  selectedFolder,
}: Props) => {
  const { createUploadProcess } = useCreateUploadProcess()
  const checkUploadedFiles = () => filesStore.files.length > 0
  const { policies } = useDocumentContextlessPolicies()
  const canManageDefaultEmployeeFolder = policies?.employeeManageDefaultEmployeeFolder.can

  const checkSubmit = (
    uploadType: UPLOAD_TYPE,
    combineFileName: string | undefined,
    folderId: number | null
  ) => {
    const mandatoryFolder = folderId == null ? canManageDefaultEmployeeFolder : true
    const mandatoryFilename =
      (uploadType !== ALL_UPLOAD_TYPES.split && uploadType !== ALL_UPLOAD_TYPES.combine) ||
      combineFileName !== ''

    return checkUploadedFiles() && mandatoryFilename && mandatoryFolder
  }

  const onSubmit = async ({
    distributionWorkflow,
    uploadType,
    folderId,
    combineFileName,
    numberSplitPages,
    esignatureRequest,
    additionalSignersAccessIds,
  }: WizardData) => {
    const result = await createUploadProcess({
      distributionWorkflow,
      files: filesStore.files,
      uploadType,
      folderId,
      combineFileName,
      numberSplitPages,
      esignatureRequest,
      additionalSignersAccessIds,
    })

    const handled = handleResult(result, (errors) => {
      const errorMessages = Object.values(errors)
      const errorMessage = errorMessages && errorMessages[0]

      if (errorMessage) app.setFlash({ type: 'wrong', message: errorMessage })
    })

    if (handled.success) {
      await onUploadFinished()
    }

    return handled
  }

  const splittingSettings = (props: WizardStepRenderParams<WizardData>) => {
    return wizardData.distributionWorkflow === 'payslips' ? (
      <ManualSettings {...props} />
    ) : (
      <SplittingSettings {...props} />
    )
  }

  const fileUploaderStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.form.documents'),
    isCompleted: () => checkUploadedFiles(),
    nextText: i18n.t('common.next'),
    render: (props) => (
      <FileUploader
        {...props}
        isReadOnly={isReadOnly}
        onDeleteFile={onDeleteFile}
        onDropFiles={onDropFiles}
      />
    ),
  }

  const splittingStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.sidepanel.splitting'),
    isCompleted: () => checkUploadedFiles(),
    nextText: i18n.t('common.next'),
    render: (props) => splittingSettings(props),
  }

  const settingsStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.sidepanel.settings'),
    isCompleted: ({ uploadType, combineFileName, folderId }) =>
      checkSubmit(uploadType, combineFileName, folderId) ?? false,
    nextText: i18n.t('split_documents.sidepanel.upload_files'),
    render: (props) => (
      <UploadSettings {...props} folders={folders} selectedFolder={selectedFolder} />
    ),
  }

  return (
    <Wizard.Modal
      title={i18n.t('split_documents.summary.title_v2')}
      icon="common-file-text-upload"
      initialData={wizardData}
      onSubmit={onSubmit}
      onClose={onClose}
      steps={[fileUploaderStep, splittingStep, settingsStep]}
    />
  )
}

export default DocumentLocalStepsModal
